/**
 * DashScope Z-Image-Turbo（图生图：同步）
 *
 * 关键点：后端错误提示 `num_images_per_prompt must be 1`
 * 因此这里强制写死 `num_images_per_prompt: 1`，避免触发异常。
 */
import axios from 'axios'
import { extractApiErrorMessage } from '../../extract-api-error-message'

var GENERATION_PATH = '/api/v1/services/aigc/multimodal-generation/generation'
var DEFAULT_MODEL = 'z-image-turbo'

function getDashScopeWanConfig() {
  return {
    baseURL: (process.env.VUE_APP_OFFICIAL_DASHSCOPE_WAN_BASE_URL || 'https://dashscope-intl.aliyuncs.com').replace(/\/$/, ''),
    apiKey: (process.env.VUE_APP_OFFICIAL_DASHSCOPE_WAN_API_KEY || '').trim()
  }
}

function parseSize(sizeStr) {
  // Z-Image: '1120*1440'
  if (!sizeStr || typeof sizeStr !== 'string') return null
  var s = sizeStr.trim()
  var m = s.match(/^(\d+)\*(\d+)$/)
  if (!m) return null
  return { width: parseInt(m[1], 10), height: parseInt(m[2], 10) }
}

function normalizeSeed(seed) {
  var s = parseInt(seed, 10)
  if (isNaN(s)) s = 0
  if (s < 0) s = 0
  var max = 2147483647
  if (s > max) s = max
  return s
}

function guessExtAndMime(blob, url) {
  var mimeType = blob && blob.type ? String(blob.type) : ''
  var ext = 'png'
  if (mimeType) {
    if (mimeType.indexOf('jpeg') !== -1) { ext = 'jpg'; mimeType = 'image/jpeg' }
    else if (mimeType.indexOf('png') !== -1) { ext = 'png'; mimeType = 'image/png' }
    else if (mimeType.indexOf('webp') !== -1) { ext = 'webp'; mimeType = 'image/webp' }
  }
  if (!mimeType) {
    try {
      var path = new URL(url).pathname
      var m = path.match(/\.(png|jpe?g|webp)(?:\?|$)/i)
      if (m) {
        var e = String(m[1]).toLowerCase()
        ext = e === 'jpeg' ? 'jpg' : e
      }
    } catch (e) {}
  }
  if (!mimeType) {
    if (ext === 'jpg') mimeType = 'image/jpeg'
    else if (ext === 'webp') mimeType = 'image/webp'
    else mimeType = 'image/png'
  }
  return { ext: ext, mimeType: mimeType }
}

function extractDashScopeImageUrls(data) {
  var urls = []
  var output = data && data.output ? data.output : null
  var choices = output && Array.isArray(output.choices) ? output.choices : []

  choices.forEach(function (choice) {
    var message = choice && choice.message ? choice.message : null
    var content = message && Array.isArray(message.content) ? message.content : []
    content.forEach(function (item) {
      var u = item && item.image ? String(item.image) : ''
      if (u) urls.push(u)
    })
  })

  return urls
}

var DASHSCOPE_OSS_HOST = 'dashscope-result-sgp.oss-ap-southeast-1.aliyuncs.com'

function proxyDashScopeOssUrl(url) {
  try {
    var u = new URL(url)
    if (u.host === DASHSCOPE_OSS_HOST) {
      return '/ossDashscopeProxy' + u.pathname + u.search
    }
  } catch (e) {}
  return url
}

function downloadUrlToBlob(url, baseName) {
  var fetchUrl = proxyDashScopeOssUrl(url)
  console.log('[ZImageTurbo] download start', { originalUrl: url, fetchUrl: fetchUrl })
  return fetch(fetchUrl)
    .then(function (res) {
      console.log('[ZImageTurbo] fetch response', { status: res.status, ok: res.ok, contentType: res.headers.get('content-type') })
      if (!res.ok) return Promise.reject(new Error('download image failed: HTTP ' + res.status))
      return res.blob()
    })
    .then(function (blob) {
      if (!blob || !blob.size) return Promise.reject(new Error('download image failed: empty blob'))
      console.log('[ZImageTurbo] image blob downloaded', blob.size, 'bytes')
      return blob
    })
}

/**
 * @param {Object} params - UI 传参
 * @param {string} params.prompt
 * @param {string} params.size - '1120*1440'
 * @param {number} params.seed - [0, 2147483647]
 * @param {boolean} params.promptExtend
 * @returns {Promise<{ urls: string[], provider:'official', sync:true }>}
 */
export function submitDashScopeZImageTurboImage(params) {
  var cfg = getDashScopeWanConfig()
  if (!cfg.baseURL) return Promise.reject(new Error('未配置 VUE_APP_OFFICIAL_DASHSCOPE_WAN_BASE_URL'))
  if (!cfg.apiKey) return Promise.reject(new Error('未配置 VUE_APP_OFFICIAL_DASHSCOPE_WAN_API_KEY'))

  var prompt = (params.prompt || '').trim()
  if (!prompt) return Promise.reject(new Error('prompt 必填'))

  // 复用你这次页面的限制思路：prompt 不要太长，保证展示/历史一致
  prompt = prompt.slice(0, 800)

  var size = String(params.size || '').trim()
  var parsedSize = parseSize(size)
  if (!parsedSize) return Promise.reject(new Error('size 必填且格式为 1024*1536'))
  if (!parsedSize.width || !parsedSize.height) return Promise.reject(new Error('size 解析失败'))

  var seed = normalizeSeed(params.seed)
  var promptExtend = params.promptExtend !== undefined && params.promptExtend !== null ? !!params.promptExtend : false

  var body = {
    model: DEFAULT_MODEL,
    input: {
      messages: [
        {
          role: 'user',
          content: [{ text: prompt }]
        }
      ]
    },
    parameters: {
      prompt_extend: promptExtend,
      size: size,
      seed: seed,
      // 关键：强制只生成 1 张，避免触发 num_images_per_prompt must be 1
      num_images_per_prompt: 1
    }
  }

  return axios
    .post(cfg.baseURL + GENERATION_PATH, body, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + cfg.apiKey
      },
      timeout: 180000
    })
    .then(function (res) {
      var data = res && res.data ? res.data : null
      var urls = extractDashScopeImageUrls(data)
      if (!urls.length) return Promise.reject(new Error('官方接口未返回图片 URL'))

      var baseName = 'z-image-turbo-' + Date.now()
      return Promise.all(
        urls.map(function (u, idx) {
          return downloadUrlToBlob(u, baseName + '-' + (idx + 1))
        })
      ).then(function (blobs) {
        return { urls: urls, blobs: blobs, provider: 'official', sync: true }
      })
    })
    .catch(function (err) {
      var msg = extractApiErrorMessage(err && err.response && err.response.data)
      msg = msg || (err && err.message) || '请求失败'
      return Promise.reject(new Error(msg))
    })
}

